import { useMemo, useState } from "react";

import { DeleteWithConfirmationDialog } from "@impero-com/common/components/deleteWithConfirmationDialog/DeleteWithConfirmationDialog";
import { withIdParams } from "@impero-com/common/components/routerUtils";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    actionMenuStatus,
    ActionSpec,
    FormBox,
    FormFrame,
    renderMarkdown,
} from "@impero-com/ui";
import { useNavigate } from "react-router-dom";

import { EntityViewDto, UpdatableEntity } from "api/generated/model";
import { AccessRights, AccessRightsValue } from "components/accessRights";
import { StringInput } from "components/form/legacyFields/stringInput";
import { TextAreaInput } from "components/form/legacyFields/textAreaInput";
import IconDelete from "img/delete.svg";
import { useDeleteEntityMutation } from "org/entities/queries/useDeleteEntityMutation";
import { useEntityQuery } from "org/entities/queries/useEntityQuery";
import { useUpdateEntityMutation } from "org/entities/queries/useUpdateEntityMutation";
import {
    convertAdminEditViewAccessRightsToUserAndRights,
    convertUsersAndRightsToAdminEditViewAccessRights,
} from "utils/accessRights/converters";
import {
    RiskEntityAccessRightsInstance,
    RiskEntityAccessRights,
} from "utils/accessRights/models";
import { FormCreate, FormRootComponentProps } from "utils/form";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";
import { ENTITIES_HASH } from "../structure/manager";
import { RISK_MODULE } from "../url";

import { checkRemoteNameAvailability } from "./utils";

function convAccessRightsToEditor(
    entity: EntityViewDto
): AccessRightsValue<RiskEntityAccessRights> {
    const {
        content: {
            resource: { responsibleId },
        },
        linkedResources: { riskAccessRights, users },
    } = entity;
    const usersAndRights = convertAdminEditViewAccessRightsToUserAndRights(
        users,
        riskAccessRights
    );
    return {
        responsible: users.find(
            (user) => user.id === (responsibleId ?? undefined)
        ),
        usersAndRights,
    };
}

function convAccessRightsToBackend(
    accessRights: AccessRightsValue<RiskEntityAccessRights>
): UpdatableEntity {
    const { responsible, usersAndRights } = accessRights;
    return {
        responsibleId: responsible?.id,
        riskAccessRights:
            convertUsersAndRightsToAdminEditViewAccessRights(usersAndRights),
    };
}

interface EntityEditorFormProps extends FormRootComponentProps<object> {
    id: string;
}

function EntityEditorForm(props: EntityEditorFormProps) {
    const { id, form, wrappedComponentRef } = props;

    const { t } = useTranslation();

    const { data: entity } = useEntityQuery(Number(id));

    const { deleteAction, deleteDialogComponent } = useDeleteAction(entity);

    return (
        <OrgApplicationBar
            title={{
                title: entity.content.resource.name,
                breadCrumbs: [
                    {
                        label: t("RiskDirectory_RiskManager"),
                        url: RISK_MODULE,
                    },
                ],
            }}
            actions={{
                id: "entity_actions",
                actions: [deleteAction],
            }}
        >
            {deleteDialogComponent}
            <View
                entity={entity}
                form={form}
                wrappedComponentRef={wrappedComponentRef}
            />
        </OrgApplicationBar>
    );
}

interface ViewProps extends FormRootComponentProps<object> {
    entity: EntityViewDto;
}

function View(props: ViewProps) {
    const { entity } = props;

    const accessRights = convAccessRightsToEditor(entity);
    const {
        edit: {
            riskAccessRights: canEditRiskAccessRights,
            content: canEditContent,
        },
    } = entity.content.actions;
    const resourceAccessRights = RiskEntityAccessRightsInstance;

    const { t } = useTranslation();

    const checkNameAvailability = useMemo(
        () =>
            async (name: string | undefined): Promise<void> => {
                if (name === undefined) {
                    return; // Should be ruled-out by required: true
                }

                if (entity.content.resource.name === name) {
                    return; // name hasn't changed, so no need to check
                }

                await checkRemoteNameAvailability(
                    name,
                    t("Entities_Edit_Error_EntityAlreadyExists")
                );
            },
        [entity, t]
    );

    const { mutateAsync } = useUpdateEntityMutation();

    return (
        <FormFrame>
            <FormBox>
                {canEditContent ? (
                    <StringInput
                        type="EDIT"
                        id="name"
                        label={t("Entity_Label_Name")}
                        value={entity.content.resource.name}
                        validators={[
                            {
                                kind: "ruleset",
                                required: true,
                                whitespace: true,
                            },
                            {
                                kind: "async",
                                asyncValidator: checkNameAvailability,
                            },
                        ]}
                        onChange={(name) => {
                            if (name !== entity.content.resource.name) {
                                mutateAsync({
                                    entityId: entity.content.resource.id,
                                    data: { name },
                                });
                            }
                        }}
                    />
                ) : (
                    <StringInput
                        type="READ_ONLY"
                        label={t("Entity_Label_Name")}
                        value={entity.content.resource.name}
                    />
                )}
            </FormBox>
            <FormBox>
                {canEditContent ? (
                    <TextAreaInput
                        type="EDIT"
                        id="description"
                        label={t("RiskDirectory_Label_Description")}
                        value={entity.content.resource.description ?? undefined}
                        onChange={(description) => {
                            if (
                                description !==
                                entity.content.resource.description
                            ) {
                                mutateAsync({
                                    entityId: entity.content.resource.id,
                                    data: { description },
                                });
                            }
                        }}
                    />
                ) : (
                    <TextAreaInput
                        type="READ_ONLY"
                        label={t("RiskDirectory_Label_Description")}
                        value={entity.content.resource.description ?? undefined}
                    />
                )}
            </FormBox>
            <FormBox>
                {canEditRiskAccessRights ? (
                    <AccessRights
                        type="EDIT"
                        id="accessRights"
                        value={accessRights}
                        onChange={(accessRights) =>
                            mutateAsync({
                                entityId: entity.content.resource.id,
                                data: convAccessRightsToBackend(accessRights),
                            })
                        }
                        resourceAccessRights={resourceAccessRights}
                    />
                ) : (
                    <AccessRights
                        type="READ_ONLY"
                        value={accessRights}
                        resourceAccessRights={resourceAccessRights}
                    />
                )}
            </FormBox>
        </FormFrame>
    );
}

function useDeleteAction(entity: EntityViewDto) {
    const { t } = useTranslation();
    const { mutateAsync: mutateDelete, isPending: isDeleting } =
        useDeleteEntityMutation();
    const navigate = useNavigate();

    const [dialogOpen, setDialogOpen] = useState(false);

    const handleDeleteClick = async () => {
        await mutateDelete({ entityId: entity.content.resource.id });
        navigate(RISK_MODULE + ENTITIES_HASH);
    };

    const deleteAction: ActionSpec = {
        id: "entity_delete_action",
        label: t("RiskEntity_Action_Delete"),
        icon: { svg: IconDelete },
        status: actionMenuStatus(
            entity.content.actions.delete,
            t("Entity_Delete_Forbidden")
        ),
        onClick: () => setDialogOpen(true),
    };

    const deleteDialogComponent = (
        <DeleteWithConfirmationDialog
            title={t("RiskEntity_Delete_Name")}
            open={dialogOpen}
            onConfirm={() => void handleDeleteClick()}
            onCancel={() => setDialogOpen(false)}
            loading={isDeleting}
        >
            {renderMarkdown(
                t("Entity_Delete_Body", {
                    name: entity.content.resource.name,
                })
            )}
        </DeleteWithConfirmationDialog>
    );
    return {
        deleteAction,
        deleteDialogComponent,
    };
}

export const EntityEditor = withIdParams(
    FormCreate<object, EntityEditorFormProps>()(EntityEditorForm)
);
